import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, AlertTriangle, Bell, ShieldCheck } from 'lucide-react';
import { useLocalization } from '@/hooks/useLocalization';

interface Policy {
  id: string;
  name: string;
  max_hours: number;
  applies_to: string;
  is_active: boolean;
}

interface Breach {
  policy_id: string;
  policy_name: string;
  ai_output_id: string;
  output_title: string;
  output_type: string;
  hours_pending: number;
}

const emptyForm = { name: '', max_hours: '24', applies_to: 'all' };

export const PolicyEngine = () => {
  const { t } = useLocalization();
  const { tenantId, session } = useAuth();
  const { toast } = useToast();
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [breaches, setBreaches] = useState<Breach[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [sendingAlerts, setSendingAlerts] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [policiesRes, breachesRes] = await Promise.all([
      supabase.from('governance_policies').select('*').order('created_at', { ascending: false }),
      supabase.rpc('check_policy_breaches', { p_tenant_id: tenantId }),
    ]);
    if (!policiesRes.error) setPolicies((policiesRes.data ?? []) as Policy[]);
    if (!breachesRes.error) setBreaches((breachesRes.data ?? []) as Breach[]);
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.name.trim()) { toast({ title: t('Policy name required'), variant: 'destructive' }); return; }
    const maxHours = Number(form.max_hours);
    if (!maxHours || maxHours <= 0) { toast({ title: t('Max hours must be a positive number'), variant: 'destructive' }); return; }

    setSaving(true);
    const { error } = await supabase.from('governance_policies').insert({
      name: form.name.trim(),
      max_hours: maxHours,
      applies_to: form.applies_to.trim() || 'all',
      tenant_id: tenantId,
    });
    setSaving(false);
    if (error) { toast({ title: t('Save failed'), description: error.message, variant: 'destructive' }); return; }
    toast({ title: t('Policy created') });
    setDialogOpen(false);
    setForm(emptyForm);
    load();
  };

  const toggleActive = async (policy: Policy) => {
    const { error } = await supabase
      .from('governance_policies')
      .update({ is_active: !policy.is_active })
      .eq('id', policy.id);
    if (error) { toast({ title: t('Update failed'), description: error.message, variant: 'destructive' }); return; }
    load();
  };

  // No scheduler triggers breach-checking automatically (same gap Sprint
  // H.5 found for run-scheduled-syncs) — this button is the real dispatch
  // path today.
  const sendAlertsNow = async () => {
    if (!session) return;
    setSendingAlerts(true);
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-governance-policies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send alerts');
      toast({
        title: data.notified > 0 ? t('Sent {0} alert(s)', { 0: data.notified }) : t('No new breaches to notify'),
        description: t('{0} current breach(es) checked.', { 0: data.breaches }),
      });
      load();
    } catch (error) {
      toast({ title: t('Failed to send alerts'), description: error instanceof Error ? error.message : t('Unknown error'), variant: 'destructive' });
    } finally {
      setSendingAlerts(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              
              {t('Governance Policies')}
            </CardTitle>
            <CardDescription>{t('Rules like review SLAs, checked live — no scheduler required to view breach status')}</CardDescription>
          </div>
          <Button size="sm" className="gap-2" onClick={() => setDialogOpen(true)}>
            <Plus className="h-4 w-4" />  {t('New Policy')}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : policies.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-muted-foreground text-sm">
              <p>{t('No policies defined yet.')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {policies.map((p) => (
                <div key={p.id} className="flex items-center justify-between px-6 py-3">
                  <div>
                    <p className="font-medium text-sm">{p.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.applies_to === 'all' ? t('All outputs') : p.applies_to}  {t('· review within')} {p.max_hours}h
                    </p>
                  </div>
                  <Switch checked={p.is_active} onCheckedChange={() => toggleActive(p)} />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              
              {t('Current Breaches')}
            </CardTitle>
            <CardDescription>{breaches.length}  {t('output(s) currently exceed their policy\'s review SLA')}</CardDescription>
          </div>
          <Button size="sm" variant="outline" className="gap-2" onClick={sendAlertsNow} disabled={sendingAlerts || breaches.length === 0}>
            {sendingAlerts ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
            
            {t('Send Alerts Now')}
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {breaches.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-muted-foreground text-sm">
              <p>{t('No breaches — everything is within policy.')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {breaches.map((b) => (
                <div key={`${b.policy_id}-${b.ai_output_id}`} className="flex items-center justify-between px-6 py-3">
                  <div>
                    <p className="font-medium text-sm">{b.output_title}</p>
                    <p className="text-xs text-muted-foreground">
                      {b.output_type}  {t('· policy "')}{b.policy_name}"
                    </p>
                  </div>
                  <Badge variant="destructive" className="text-xs">{b.hours_pending}{t('h pending')}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('New Governance Policy')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t('Policy Name *')}</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t('e.g. Reports reviewed within 24h')} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('Max Hours to Review')}</Label>
                <Input type="number" min={1} value={form.max_hours} onChange={(e) => setForm((f) => ({ ...f, max_hours: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>{t('Applies To')}</Label>
                <Input value={form.applies_to} onChange={(e) => setForm((f) => ({ ...f, applies_to: e.target.value }))} placeholder="all, or an output_type" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('Cancel')}</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin me-2" /> : null}{t('Create Policy')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PolicyEngine;
